import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Table } from './ui/Table';
import { apiList, apiCreate, apiUpdate, apiDelete } from '../api/apiClient';
import { getPDKSystemHolders, getPDKLiveDevices } from '../api/pdkApi';
import { logAuditEvent, getAuditEventsForSystem } from '../api/auditLog';
import { sendBridgeCommand } from '../api/bridgeApi';
// Seam API imports removed — Seam integration disabled
import { AccessCodeManager } from './locks/AccessCodeManager';
import { LockEventLog } from './locks/LockEventLog';
import { LOCK_BRANDS } from './locks/BrandPicker';
import { refreshAllCounters } from '../utils/refreshCounters';
import { DeviceActionButton, intrlockTypeLabel, getIntrlockType } from './access/DeviceVisuals';
import { pollAfterAction } from '../utils/postActionPoll';

interface Props {
  open: boolean;
  onClose: () => void;
  system: any;
  properties: any[];
  onTestConnection?: (system: any) => void;
  testing?: number | null;
  /** Called after a successful save/delete so the parent list re-fetches. */
  onMutated?: () => void;
}

type TabKey = 'overview' | 'devices' | 'people' | 'audit';

const EVENT_TYPE_COLORS: Record<string, string> = {
  system_connected: 'blue',
  device_added: 'green',
  device_removed: 'red',
  door_unlocked: 'green',
  door_locked: 'yellow',
  holder_imported: 'purple',
  tenant_added: 'green',
  tenant_removed: 'red',
};

export const SystemDetailModal: React.FC<Props> = ({ open, onClose, system, properties, onTestConnection, testing, onMutated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPropertyId, setEditPropertyId] = useState<string>('');
  const [editStatus, setEditStatus] = useState<string>('active');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState<TabKey>('overview');
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [auditEvents, setAuditEvents] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [holders, setHolders] = useState<any[]>([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [holdersFetched, setHoldersFetched] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [selectedHolders, setSelectedHolders] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState('');

  const [addingDevices, setAddingDevices] = useState(false);
  const [pdkAllDevices, setPdkAllDevices] = useState<any[]>([]);
  const [selectedNewDevices, setSelectedNewDevices] = useState<Set<string>>(new Set());
  const [savingDevices, setSavingDevices] = useState(false);
  const [intrlockDeviceMap, setIntrlockDeviceMap] = useState<Record<string, number>>({}); // pdk_device_id -> intrlock record id
  const [removingDeviceId, setRemovingDeviceId] = useState<string | null>(null);
  const [channelNames, setChannelNames] = useState<Record<number, string>>({});
  const [editingChannel, setEditingChannel] = useState<number | null>(null);
  const [savingChannel, setSavingChannel] = useState(false);
  const [firingChannel, setFiringChannel] = useState<number | null>(null);
  const [heldChannels, setHeldChannels] = useState<Set<number>>(new Set());
  const [lockStatuses, setLockStatuses] = useState<Record<string, { locked: boolean; online: boolean; battery?: number }>>({});
  const [lockToggling, setLockToggling] = useState<string | null>(null);
  const [selectedLockDeviceId, setSelectedLockDeviceId] = useState<string | null>(null);

  const property = properties.find((p: any) => p.id === system?.property_id);
  // Resolve PDK system ID: prefer system record, fall back to property's pdk_system_id
  const pdkSystemId = system?.system_id || system?.pdk_system_id || property?.pdk_system_id || '';
  const isPDK = system?.provider === 'pdk' && !!pdkSystemId;

  // Parse stored cloud node IDs from the system description
  const storedMeta = (() => {
    try { return JSON.parse(system?.description || '{}'); } catch { return {}; }
  })();
  const storedCloudNodeIds: string[] = storedMeta.cloud_node_ids || [];
  const storedDeviceIds: string[] = storedMeta.device_ids || [];

  useEffect(() => {
    if (!open || !system) return;
    setTab('overview');
    setShowImportPreview(false);
    setImportResult('');
    setHoldersFetched(false);
    setHolders([]);
    setEditingChannel(null);
    // Load saved channel names from system config
    const savedNames: Record<number, string> = {};
    if (system?.channel_config_json) {
      const cfg = typeof system.channel_config_json === 'string' ? JSON.parse(system.channel_config_json) : system.channel_config_json;
      if (cfg?.channel_names) Object.assign(savedNames, cfg.channel_names);
    }
    setChannelNames(savedNames);
    loadDevices();
    loadTenants();
    loadAuditEvents();
  }, [open, system?.id]);

  // Auto-fetch PDK holders when we have a system ID
  useEffect(() => {
    if (!open || !isPDK) return;
    loadHoldersQuiet();
  }, [open, pdkSystemId]);

  const loadHoldersQuiet = async () => {
    if (!pdkSystemId) return;
    try {
      const h = await getPDKSystemHolders(pdkSystemId);
      setHolders(h);
      setHoldersFetched(true);
    } catch {
      setHoldersFetched(true);
    }
  };

  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      if (isPDK) {
        // Pass stored cloud node IDs to filter devices to only selected nodes
        const nodeFilter = storedCloudNodeIds.length > 0 ? storedCloudNodeIds : undefined;
        const liveDevices = await getPDKLiveDevices(pdkSystemId, nodeFilter);
        // Check which PDK devices have been added to Intrlock
        const intrlockDevices = await apiList('devices');
        const propertyPdkDevices = (Array.isArray(intrlockDevices) ? intrlockDevices : [])
          .filter((d: any) => d.property_id === system?.property_id && d.acs_provider === 'pdk');
        const addedPdkIds = new Set(propertyPdkDevices.map((d: any) => String(d.acs_device_id)));
        // Build mapping: PDK device ID -> Intrlock record ID (for removal)
        const idMap: Record<string, number> = {};
        for (const d of propertyPdkDevices) {
          if (d.acs_device_id) idMap[String(d.acs_device_id)] = d.id;
        }
        setIntrlockDeviceMap(idMap);
        // Mark each live device as added or not
        const enriched = liveDevices.map((d: any) => ({
          ...d,
          _added_to_intrlock: addedPdkIds.has(String(d.id)),
        }));
        setPdkAllDevices(enriched);
        // Show only added devices in the main table
        setDevices(addedPdkIds.size > 0
          ? enriched.filter((d: any) => d._added_to_intrlock)
          : enriched
        );
      } else {
        const all = await apiList('devices');
        const filtered = (Array.isArray(all) ? all : []).filter(
          (d: any) => d.property_id === system?.property_id
        );
        setDevices(filtered);
      }
    } catch (e) {
      console.error('Failed to load devices:', e);
    } finally {
      setDevicesLoading(false);
    }
  };

  const loadTenants = async () => {
    setTenantsLoading(true);
    try {
      const all = await apiList('tenants');
      const filtered = (Array.isArray(all) ? all : []).filter(
        (t: any) => t.property_id === system?.property_id
      );
      setTenants(filtered);
    } catch (e) {
      console.error('Failed to load tenants:', e);
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadAuditEvents = async () => {
    setAuditLoading(true);
    try {
      const events = await getAuditEventsForSystem(system?.id, system?.property_id);
      setAuditEvents(events);
    } catch (e) {
      console.error('Failed to load audit events:', e);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    // Seam device status fetching removed — Seam integration disabled
  }, [open, tab, devices]);

  const handleSmartLockToggle = async (deviceId: string) => {
    // Smart lock toggle removed — Seam integration disabled
  };

  const importedHolderIds = new Set(tenants.filter((t: any) => t.pdk_holder_id).map((t: any) => t.pdk_holder_id));
  const importedFromPDK = tenants.filter((t: any) => t.pdk_holder_id || t.acs_provider === 'pdk');
  const manuallyAdded = tenants.filter((t: any) => !t.pdk_holder_id && t.acs_provider !== 'pdk');
  const availableToImport = holdersFetched ? holders.filter((h: any) => !importedHolderIds.has(String(h.id))) : [];

  const fetchHolders = async () => {
    if (!isPDK) return;
    setHoldersLoading(true);
    try {
      let h = holders;
      if (!holdersFetched || h.length === 0) {
        h = await getPDKSystemHolders(pdkSystemId);
        setHolders(h);
        setHoldersFetched(true);
      }
      // Pre-select only holders that haven't been imported yet
      const notImported = h.filter((x: any) => !importedHolderIds.has(String(x.id)));
      setSelectedHolders(new Set(notImported.map((x: any) => String(x.id))));
      setShowImportPreview(true);
    } catch (e: any) {
      setImportResult(`Error fetching holders: ${e.message}`);
    } finally {
      setHoldersLoading(false);
    }
  };

  const importSelected = async () => {
    setImporting(true);
    setImportResult('');
    let imported = 0;
    try {
      for (const holderId of Array.from(selectedHolders)) {
        const holder = holders.find((h: any) => String(h.id) === holderId);
        if (!holder) continue;
        // Check if already imported
        const existing = tenants.find((t: any) => t.pdk_holder_id === String(holder.id));
        if (existing) continue;
        // Cache non-sensitive PDK metadata (no credentials, PINs, or card numbers)
        const pdkMeta: any = {
          pdk_system_id: pdkSystemId,
          pdk_system_name: system.name,
          holder_id: holder.id,
          partition: holder.partition || null,
          enabled: holder.enabled !== false,
          photo_url: holder.photo || null,
          start_date: holder.startDate || null,
          end_date: holder.endDate || null,
          groups: (holder.groups || []).map((g: any) => ({
            id: g.id,
            name: g.name,
            description: g.description || null,
          })),
          custom_fields: holder.customFields || null,
          imported_at: new Date().toISOString(),
        };
        await apiCreate('tenants', {
          first_name: holder.firstName || '',
          last_name: holder.lastName || '',
          email: holder.email || '',
          phone: holder.phone || holder.mobilePhone || '',
          is_primary: false,
          status: holder.enabled !== false ? 'active' : 'inactive',
          pdk_holder_id: String(holder.id),
          // Generic external id so the same person can be matched
          // back to ANY ACS provider, not just PDK. Used by call /
          // unlock paths that look up by acs_provider+external_id.
          external_id: String(holder.id),
          acs_provider: 'pdk',
          property_id: system.property_id,
          user_id: 0,
          pdk_metadata_json: pdkMeta,
        });
        imported++;
      }
      await logAuditEvent({
        property_id: system.property_id,
        acs_system_id: system.id,
        event_type: 'holder_imported',
        event_source: 'pdk',
        description: `Imported ${imported} holders from PDK system "${system.name}"`,
        metadata: { count: imported, system_id: pdkSystemId },
      });
      refreshAllCounters();
      setImportResult(`Successfully imported ${imported} holder(s).`);
      setShowImportPreview(false);
      loadTenants();
      loadAuditEvents();
    } catch (e: any) {
      setImportResult(`Import error: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const toggleHolder = (id: string) => {
    const s = new Set(selectedHolders);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedHolders(s);
  };

  // Sync edit form when the selected system changes.
  useEffect(() => {
    if (!system) return;
    setEditName(system.name || '');
    setEditPropertyId(String(system.property_id || ''));
    setEditStatus(system.status || 'active');
    setIsEditing(false);
  }, [system]);

  const handleSaveEdit = async () => {
    if (!system) return;
    setSavingEdit(true);
    try {
      await apiUpdate('access_control_systems', system.uuid || system.id, {
        name: editName.trim() || system.name,
        property_id: editPropertyId || system.property_id,
        status: editStatus,
      });
      // Mirror locally so the modal reflects the change immediately.
      system.name = editName.trim() || system.name;
      system.property_id = editPropertyId || system.property_id;
      system.status = editStatus;
      setIsEditing(false);
      refreshAllCounters();
      onMutated?.();
    } catch (err: any) {
      alert(`Failed to save: ${err?.message || err}`);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!system) return;
    if (!window.confirm(`Delete "${system.name}"? This removes the access control system. Devices and people remain — only the integration record is deleted.`)) {
      return;
    }
    setDeleting(true);
    try {
      await apiDelete('access_control_systems', system.uuid || system.id);
      refreshAllCounters();
      onMutated?.();
      onClose();
    } catch (err: any) {
      alert(`Failed to delete: ${err?.message || err}`);
    } finally {
      setDeleting(false);
    }
  };

  if (!system) return null;

  const isBridge = system.provider === 'bridge';

  const tabs: { key: TabKey; label: string }[] = isBridge
    ? [
        { key: 'overview', label: 'Overview' },
        { key: 'devices', label: `Relay Channels (${system.channel_count || 0})` },
        { key: 'audit', label: 'Audit Log' },
      ]
    : [
        { key: 'overview', label: 'Overview' },
        { key: 'devices', label: `Devices (${devices.length})` },
        { key: 'people', label: `People (${tenants.length})` },
        { key: 'audit', label: 'Audit Log' },
      ];

  return (
    <Modal open={open} onClose={onClose} title="" size="lg">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 -mt-2">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-base font-semibold"
                placeholder="System name"
              />
              <div className="flex items-center gap-2 text-sm">
                <label className="text-xs text-gray-500">Property</label>
                <select
                  value={editPropertyId}
                  onChange={(e) => setEditPropertyId(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                >
                  {properties.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <label className="text-xs text-gray-500 ml-2">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="provisioning">provisioning</option>
                  <option value="error">error</option>
                </select>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900 truncate">{system.name}</h2>
              <p className="text-sm text-gray-500">{property?.name || `Property #${system.property_id}`}</p>
            </>
          )}
        </div>
        <Badge color="blue">{(() => {
          const brand = LOCK_BRANDS.find(b => b.key === system.provider);
          return brand ? brand.name : (system.provider || '').toUpperCase();
        })()}</Badge>
        <Badge status={system.status}>{system.status}</Badge>
        {isEditing ? (
          <>
            <Button size="sm" onClick={handleSaveEdit} loading={savingEdit}>Save</Button>
            <Button size="sm" variant="secondary" onClick={() => setIsEditing(false)} disabled={savingEdit}>Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="secondary" onClick={() => setIsEditing(true)}>Edit</Button>
            <Button size="sm" variant="danger" onClick={handleDelete} loading={deleting}>Delete</Button>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4 -mx-6 px-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px] max-h-[60vh] overflow-y-auto">
        {tab === 'overview' && isBridge && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Serial Number</p>
                <p className="text-sm font-mono text-gray-900 mt-1">{system.serial_number || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                <div className="mt-1">
                  <Badge color={system.status === 'online' ? 'green' : system.status === 'provisioning' ? 'yellow' : 'gray'}>
                    {system.status || 'unknown'}
                  </Badge>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Relay Channels</p>
                <p className="text-sm text-gray-900 mt-1 font-semibold">{system.channel_count || 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Property</p>
                <p className="text-sm text-gray-900 mt-1">{property?.name || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Firmware</p>
                <p className="text-sm font-mono text-gray-900 mt-1">{system.firmware_version || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Last Seen</p>
                <p className="text-sm text-gray-900 mt-1">{system.last_seen ? new Date(system.last_seen).toLocaleString() : 'Never'}</p>
              </div>
              {system.ip_address && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">IP Address</p>
                  <p className="text-sm font-mono text-gray-900 mt-1">{system.ip_address}</p>
                </div>
              )}
              {system.wifi_ssid && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">WiFi Network</p>
                  <p className="text-sm text-gray-900 mt-1">{system.wifi_ssid}</p>
                </div>
              )}
              {system.cpu_temp != null && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">CPU Temp</p>
                  <p className="text-sm text-gray-900 mt-1">{system.cpu_temp}°C</p>
                </div>
              )}
              {system.uptime_seconds != null && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Uptime</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {system.uptime_seconds > 86400
                      ? `${Math.floor(system.uptime_seconds / 86400)}d ${Math.floor((system.uptime_seconds % 86400) / 3600)}h`
                      : system.uptime_seconds > 3600
                      ? `${Math.floor(system.uptime_seconds / 3600)}h ${Math.floor((system.uptime_seconds % 3600) / 60)}m`
                      : `${Math.floor(system.uptime_seconds / 60)}m`}
                  </p>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Connected Since</p>
                <p className="text-sm text-gray-900 mt-1">{system.claimed_at ? new Date(system.claimed_at).toLocaleString() : system.created_at ? new Date(system.created_at).toLocaleString() : '—'}</p>
              </div>
            </div>
          </div>
        )}

        {tab === 'overview' && !isBridge && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">System Name</p>
                <p className="text-sm text-gray-900 mt-1 truncate">{system.name || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Provider</p>
                <p className="text-sm text-gray-900 mt-1">{(system.provider || '').toLowerCase() === 'pdk' ? 'ProDataKey' : (system.provider || '').toLowerCase() === 'brivo' ? 'Brivo' : (system.provider || '').toUpperCase()}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Property</p>
                <p className="text-sm text-gray-900 mt-1">{property?.name || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                <div className="mt-1"><Badge status={system.status}>{system.status}</Badge></div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Devices</p>
                <p className="text-sm text-gray-900 mt-1">{devices.length}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Last Sync</p>
                <p className="text-sm text-gray-900 mt-1">{system.last_sync ? new Date(system.last_sync).toLocaleString() : '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Connected Since</p>
                <p className="text-sm text-gray-900 mt-1">{system.created_at ? new Date(system.created_at).toLocaleString() : '—'}</p>
              </div>
            </div>
            {/* Alerts */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">System Alerts</p>
              {(() => {
                const alerts: { label: string; color: string }[] = [];
                const offlineDevices = devices.filter((d: any) => d.connection_status === 'offline' || d.connection_status === 'disconnected');
                if (offlineDevices.length > 0) alerts.push({ label: `${offlineDevices.length} device${offlineDevices.length > 1 ? 's' : ''} offline`, color: 'red' });
                const lowBattery = devices.filter((d: any) => d.battery_level != null && d.battery_level < 20);
                if (lowBattery.length > 0) alerts.push({ label: `${lowBattery.length} low battery`, color: 'yellow' });
                if (system.status === 'disconnected') alerts.push({ label: 'System disconnected', color: 'red' });
                if (!system.last_sync) alerts.push({ label: 'Never synced', color: 'gray' });
                else {
                  const hrs = (Date.now() - new Date(system.last_sync).getTime()) / 3600000;
                  if (hrs > 24) alerts.push({ label: 'Last sync > 24h ago', color: 'yellow' });
                }
                if (alerts.length === 0) alerts.push({ label: 'All systems healthy', color: 'green' });
                return (
                  <div className="flex flex-wrap gap-2">
                    {alerts.map((a, i) => (
                      <span key={i} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        a.color === 'red' ? 'bg-red-50 text-red-700' :
                        a.color === 'yellow' ? 'bg-yellow-50 text-yellow-700' :
                        a.color === 'green' ? 'bg-green-50 text-green-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          a.color === 'red' ? 'bg-red-500' :
                          a.color === 'yellow' ? 'bg-yellow-500' :
                          a.color === 'green' ? 'bg-green-500' :
                          'bg-gray-400'
                        }`}></span>
                        {a.label}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
            {onTestConnection && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={() => onTestConnection(system)}
                  loading={testing === system.id}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                  Test Connection
                </Button>
              </div>
            )}
          </div>
        )}

        {tab === 'devices' && isBridge && (() => {
          const gpioMap: Record<number, number> = { 1: 17, 2: 27, 3: 22, 4: 23, 5: 5, 6: 6, 7: 13, 8: 19 };
          const pinMap: Record<number, number> = { 1: 11, 2: 13, 3: 15, 4: 16, 5: 29, 6: 31, 7: 33, 8: 35 };
          const defaultNames: Record<number, string> = { 1: 'Front Door', 2: 'Rear Door', 3: 'Parking Gate', 4: 'Loading Dock', 5: 'Lobby', 6: 'Stairwell', 7: 'Buzzer', 8: 'Spare' };

          const saveChannelName = async (ch: number, name: string) => {
            setSavingChannel(true);
            try {
              const updated = { ...channelNames, [ch]: name };
              setChannelNames(updated);
              const existingCfg = typeof system.channel_config_json === 'string'
                ? JSON.parse(system.channel_config_json || '{}') : (system.channel_config_json || {});
              await apiUpdate('bridge_devices', system.uuid || system.id, {
                channel_config_json: { ...existingCfg, channel_names: updated },
              });
              refreshAllCounters();
              setEditingChannel(null);
            } catch (e: any) {
              console.error('Failed to save channel name:', e);
            } finally {
              setSavingChannel(false);
            }
          };

          const count = system.channel_count || 0;
          // Backend expects the bare bridge serial (e.g. BR-2604-RELAY-XXXXXXXX),
          // not the legacy `bridge-{serial}` prefixed form.
          const bridgeDeviceId = system.serial_number || system.bridge_device_id || system.id;

          const handleFireRelay = async (ch: number) => {
            setFiringChannel(ch);
            try {
              await sendBridgeCommand(bridgeDeviceId, 'pulse', ch, 5000);
              // §28.518.80 — P-OPT-2: fire-and-forget post-action poll.
              // Bridge relay state is local (no property-scoped status API);
              // poll fires the EVENT_LISTEN log marker and re-fetches ACS list.
              pollAfterAction(
                () => Promise.resolve(onMutated?.()),
                String(bridgeDeviceId),
                `pulse_relay_ch${ch}`,
              );
            } catch (e: any) {
              console.error('Failed to fire relay:', e);
            } finally {
              setTimeout(() => setFiringChannel(null), 5000);
            }
          };

          const handleToggleRelay = async (ch: number) => {
            const isHeld = heldChannels.has(ch);
            try {
              if (isHeld) {
                await sendBridgeCommand(bridgeDeviceId, 'off', ch);
                setHeldChannels(prev => { const s = new Set(prev); s.delete(ch); return s; });
              } else {
                await sendBridgeCommand(bridgeDeviceId, 'on', ch);
                setHeldChannels(prev => new Set(prev).add(ch));
              }
              // §28.518.80 — P-OPT-2: fire-and-forget post-action poll.
              const relayAction = isHeld ? 'relay_off' : 'relay_on';
              pollAfterAction(
                () => Promise.resolve(onMutated?.()),
                String(bridgeDeviceId),
                `${relayAction}_ch${ch}`,
              );
            } catch (e: any) {
              console.error('Failed to toggle relay:', e);
            }
          };

          return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{count}</p>
                <p className="text-xs text-green-600 mt-0.5">Relay Channels</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{system.status === 'online' ? 'Online' : 'Offline'}</p>
                <p className="text-xs text-emerald-600 mt-0.5">Connection</p>
              </div>
            </div>

            <div className="space-y-2">
              {Array.from({ length: count }, (_, i) => i + 1).map((ch) => {
                const displayName = channelNames[ch] || defaultNames[ch] || `Relay ${ch}`;
                const isEditing = editingChannel === ch;
                const isFiring = firingChannel === ch;
                const isHeld = heldChannels.has(ch);
                const isActive = isFiring || isHeld;
                return (
                  <div key={ch} className={`flex items-center justify-between p-3 rounded-lg border ${isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100'}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-green-200' : 'bg-brand-100'}`}>
                        <span className={`text-sm font-bold ${isActive ? 'text-green-700' : 'text-brand-700'}`}>{ch}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <form onSubmit={(e) => { e.preventDefault(); const input = (e.target as HTMLFormElement).elements.namedItem('chName') as HTMLInputElement; saveChannelName(ch, input.value); }} className="flex items-center gap-2">
                            <input name="chName" defaultValue={displayName} autoFocus
                              className="text-sm font-medium text-gray-900 border border-brand-300 rounded px-2 py-1 flex-1 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              onKeyDown={(e) => { if (e.key === 'Escape') setEditingChannel(null); }}
                            />
                            <button type="submit" disabled={savingChannel} className="text-xs text-brand-600 hover:text-brand-700 font-medium">Save</button>
                            <button type="button" onClick={() => setEditingChannel(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                          </form>
                        ) : (
                          <p className="text-sm font-medium text-gray-900">{displayName}</p>
                        )}
                        <p className="text-xs text-gray-500">GPIO {gpioMap[ch] ?? '?'} · Pin {pinMap[ch] ?? '?'} · Momentary 5s</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!isEditing && (
                        <button onClick={() => setEditingChannel(ch)} className="text-xs text-brand-600 hover:text-brand-700 font-medium">Rename</button>
                      )}
                      {!isHeld && (
                        <DeviceActionButton
                          onClick={() => handleFireRelay(ch)}
                          disabled={isFiring || isHeld || firingChannel !== null}
                          loading={isFiring}
                          loadingLabel="Unlocking…"
                        >
                          Unlock
                        </DeviceActionButton>
                      )}
                      <button
                        onClick={() => handleToggleRelay(ch)}
                        disabled={isFiring}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          isHeld ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        title={isHeld ? 'Relay ON — click to release' : 'Hold relay open'}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          isHeld ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {count === 0 && (
              <div className="text-center py-8 text-gray-400">No relay channels detected. Check wiring and restart the Bridge agent.</div>
            )}
          </div>
          );
        })()}

        {tab === 'devices' && !isBridge && (() => {
          const notAdded = isPDK ? pdkAllDevices.filter((d: any) => !d._added_to_intrlock) : [];
          const addedCount = devices.length;

          const handleRemoveDevice = async (pdkDeviceId: string) => {
            const intrlockId = intrlockDeviceMap[pdkDeviceId];
            if (!intrlockId) return;
            setRemovingDeviceId(pdkDeviceId);
            try {
              await apiDelete('devices', intrlockId);
              refreshAllCounters();
              await logAuditEvent({
                property_id: system.property_id,
                acs_system_id: system.id,
                event_type: 'device_removed',
                event_source: 'pdk',
                description: `Removed PDK device "${devices.find((d: any) => String(d.id) === pdkDeviceId)?.name || pdkDeviceId}" from "${system.name}"`,
                metadata: { pdk_device_id: pdkDeviceId, intrlock_id: intrlockId },
              });
              loadDevices();
              loadAuditEvents();
            } catch (e: any) {
              console.error('Failed to remove device:', e);
              alert('Failed to remove device: ' + e.message);
            } finally {
              setRemovingDeviceId(null);
            }
          };

          const handleAddDevices = async () => {
            setSavingDevices(true);
            try {
              let added = 0;
              for (const devId of Array.from(selectedNewDevices)) {
                const dev = pdkAllDevices.find((d: any) => String(d.id) === devId);
                if (!dev) continue;
                await apiCreate('devices', {
                  device_name: dev.name || `PDK Device ${dev.id}`,
                  device_type: dev.type || 'lock',
                  property_id: system.property_id,
                  status: 'active',
                  serial_number: `PDK-${dev.id}`,
                  connection_status: dev.connection_status || 'online',
                  acs_provider: 'pdk',
                  acs_device_id: String(dev.id),
                  model: dev.model || dev.name || '',
                  manufacturer: 'ProDataKey',
                });
                added++;
              }
              await logAuditEvent({
                property_id: system.property_id,
                acs_system_id: system.id,
                event_type: 'device_added',
                event_source: 'pdk',
                description: `Added ${added} PDK devices to "${system.name}"`,
                metadata: { count: added, device_ids: Array.from(selectedNewDevices) },
              });
              refreshAllCounters();
              setAddingDevices(false);
              setSelectedNewDevices(new Set());
              loadDevices();
              loadAuditEvents();
            } catch (e: any) {
              console.error('Failed to add devices:', e);
              alert('Failed to add devices: ' + e.message);
            } finally {
              setSavingDevices(false);
            }
          };

          return (
          <div className="space-y-4">
            {/* Device Stats */}
            {isPDK && !devicesLoading && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{addedCount}</p>
                  <p className="text-xs text-green-600 mt-0.5">Added to Intrlock</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{notAdded.length}</p>
                  <p className="text-xs text-amber-600 mt-0.5">Available to Add</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-700">{pdkAllDevices.length}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Total on Cloud Nodes</p>
                </div>
              </div>
            )}

            {/* Add More Devices */}
            {isPDK && notAdded.length > 0 && !addingDevices && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{notAdded.length} device{notAdded.length !== 1 ? 's' : ''} available to add</p>
                <Button size="sm" onClick={() => { setSelectedNewDevices(new Set(notAdded.map((d: any) => String(d.id)))); setAddingDevices(true); }}>
                  + Add {notAdded.length} Devices
                </Button>
              </div>
            )}

            {addingDevices && (
              <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-emerald-900">{notAdded.length} devices available — {selectedNewDevices.size} selected</p>
                  <button className="text-xs text-emerald-600 hover:text-emerald-800 font-medium" onClick={() => {
                    setSelectedNewDevices(selectedNewDevices.size === notAdded.length ? new Set() : new Set(notAdded.map((d: any) => String(d.id))));
                  }}>
                    {selectedNewDevices.size === notAdded.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {notAdded.map((d: any) => (
                    <label key={d.id} className="flex items-center gap-3 p-2 rounded hover:bg-emerald-100 cursor-pointer">
                      <input type="checkbox" checked={selectedNewDevices.has(String(d.id))} onChange={() => {
                        const s = new Set(selectedNewDevices);
                        s.has(String(d.id)) ? s.delete(String(d.id)) : s.add(String(d.id));
                        setSelectedNewDevices(s);
                      }} className="rounded text-emerald-600" />
                      <span className="text-sm font-medium text-gray-900 flex-1">{d.name}</span>
                      <Badge color="gray">{intrlockTypeLabel(d)}</Badge>
                      <span className={`w-2 h-2 rounded-full ${d.connection_status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="secondary" onClick={() => setAddingDevices(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleAddDevices} loading={savingDevices} disabled={selectedNewDevices.size === 0}>
                    Add {selectedNewDevices.size} Devices
                  </Button>
                </div>
              </div>
            )}

            {/* Devices Table */}
            {devicesLoading ? (
              <div className="text-center py-8 text-gray-400">Loading devices...</div>
            ) : devices.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No devices added for this system yet.</div>
            ) : (
              <Table
                columns={[
                  { key: 'name', header: 'Name', render: (d: any) => <span className="font-medium">{d.name}</span> },
                  { key: 'type', header: 'Type', render: (d: any) => <Badge color="gray">{intrlockTypeLabel(d)}</Badge> },
                  { key: 'acs_device_id', header: 'Device ID', render: (d: any) => <span className="text-xs text-gray-500 font-mono">{d.external_device_id || d.acs_device_id || d.id || '—'}</span> },
                  { key: 'cloud_node_name', header: 'Cloud Node', render: (d: any) => (
                    <span className="text-xs text-gray-600">
                      {d.cloud_node_name || '—'}{(d.external_cloud_node_id || d.cloud_node_id) ? <span className="block font-mono text-gray-400">{d.external_cloud_node_id || d.cloud_node_id}</span> : null}
                    </span>
                  )},
                  { key: 'connection_status', header: 'Connection', render: (d: any) => (
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${d.connection_status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <span className={`text-sm ${d.connection_status === 'online' ? 'text-green-700' : 'text-red-600'}`}>
                        {d.connection_status === 'online' ? 'Online' : 'Offline'}
                      </span>
                    </span>
                  )},
                  { key: 'lock_status', header: 'Lock State', render: (d: any) => {
                    if (getIntrlockType(d) !== 'Lock') return <span className="text-gray-400">—</span>;
                    const locked = d.lock_status === 'locked';
                    return (
                      <span className={`inline-flex items-center gap-1 text-sm font-medium ${locked ? 'text-green-700' : 'text-amber-600'}`}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          {locked ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          )}
                        </svg>
                        {locked ? 'Locked' : 'Unlocked'}
                      </span>
                    );
                  }},
                  { key: 'firmware_version', header: 'Firmware', render: (d: any) => <span className="text-xs text-gray-500 font-mono">{d.firmware_version || '—'}</span> },
                  { key: 'battery_percent', header: 'Battery', render: (d: any) => <span className="text-xs text-gray-500">{d.battery_percent != null ? `${d.battery_percent}%` : (d.battery_state || '—')}</span> },
                  ...(isPDK ? [{ key: '_actions', header: '', render: (d: any) => {
                    const pdkId = String(d.acs_device_id || d.id);
                    const hasIntrlockRecord = !!intrlockDeviceMap[pdkId];
                    if (!hasIntrlockRecord) return null;
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove "${d.name}" from this system? This will stop billing for this device.`)) handleRemoveDevice(pdkId); }}
                        disabled={removingDeviceId === pdkId}
                        className="text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-50"
                      >
                        {removingDeviceId === pdkId ? 'Removing...' : 'Remove'}
                      </button>
                    );
                  }}] : []),
                ]}
                data={devices}
              />
            )}

            {selectedLockDeviceId && (
              <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">Lock Details</h4>
                  <button onClick={() => setSelectedLockDeviceId(null)} className="text-xs text-gray-400 hover:text-gray-600">Close</button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Access Codes</h5>
                    <AccessCodeManager deviceId={selectedLockDeviceId} />
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Event History</h5>
                    <LockEventLog deviceId={selectedLockDeviceId} />
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {tab === 'people' && (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{importedFromPDK.length}</p>
                <p className="text-xs text-emerald-600 mt-0.5">Imported from PDK</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-700">{manuallyAdded.length}</p>
                <p className="text-xs text-gray-500 mt-0.5">Added Manually</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">
                  {holdersFetched ? availableToImport.length : '...'}
                </p>
                <p className="text-xs text-amber-600 mt-0.5">Available to Import</p>
              </div>
            </div>

            {/* Import Button */}
            {isPDK && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  {holdersFetched && holders.length > 0
                    ? `${holders.length} total holders in PDK system`
                    : holdersFetched
                    ? 'No holders found in PDK'
                    : 'Checking PDK for holders...'}
                </p>
                <Button
                  size="sm"
                  onClick={fetchHolders}
                  loading={holdersLoading}
                  disabled={holdersFetched && availableToImport.length === 0}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {availableToImport.length > 0
                    ? `Import ${availableToImport.length} from PDK`
                    : 'All Imported'}
                </Button>
              </div>
            )}

            {importResult && (
              <div className={`px-4 py-3 rounded-lg text-sm ${importResult.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {importResult}
              </div>
            )}

            {/* Import Preview */}
            {showImportPreview && (
              <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-emerald-900">
                    {availableToImport.length} new holders to import — {selectedHolders.size} selected
                  </p>
                  <button
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                    onClick={() => {
                      if (selectedHolders.size === availableToImport.length) setSelectedHolders(new Set());
                      else setSelectedHolders(new Set(availableToImport.map((h: any) => String(h.id))));
                    }}
                  >
                    {selectedHolders.size === availableToImport.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {holders.map((h: any) => {
                    const alreadyImported = importedHolderIds.has(String(h.id));
                    return (
                      <label key={h.id} className={`flex items-center gap-3 p-2 rounded ${alreadyImported ? 'opacity-40 cursor-default' : 'hover:bg-emerald-100 cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={selectedHolders.has(String(h.id))}
                          onChange={() => toggleHolder(String(h.id))}
                          disabled={alreadyImported}
                          className="rounded text-emerald-600"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">
                            {h.firstName} {h.lastName}
                          </span>
                          {h.email && <span className="text-xs text-gray-500 ml-2">{h.email}</span>}
                        </div>
                        {alreadyImported && <Badge color="gray">Already in Intrlock</Badge>}
                        <Badge color={h.enabled !== false ? 'green' : 'red'}>
                          {h.enabled !== false ? 'Active' : 'Disabled'}
                        </Badge>
                      </label>
                    );
                  })}
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="secondary" onClick={() => setShowImportPreview(false)}>Cancel</Button>
                  <Button size="sm" onClick={importSelected} loading={importing} disabled={selectedHolders.size === 0}>
                    Import {selectedHolders.size} People
                  </Button>
                </div>
              </div>
            )}

            {/* People Table */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">People with Access</h4>
              {tenantsLoading ? (
                <div className="text-center py-8 text-gray-400">Loading...</div>
              ) : tenants.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-gray-400 text-sm">No people assigned to this system yet.</p>
                  {isPDK && <p className="text-gray-400 text-xs mt-1">Use "Import from PDK" to pull holders into Intrlock.</p>}
                </div>
              ) : (
                <Table
                  columns={[
                    { key: 'name', header: 'Name', render: (t: any) => (
                      <div>
                        <span className="font-medium text-gray-900">{t.first_name} {t.last_name}</span>
                        {t.is_primary && <span className="ml-1.5 text-xs text-brand-600 font-medium">Primary</span>}
                      </div>
                    )},
                    { key: 'contact', header: 'Contact', render: (t: any) => (
                      <div className="text-sm">
                        {t.email && <p className="text-gray-600 truncate max-w-[180px]">{t.email}</p>}
                        {t.phone && <p className="text-gray-400 text-xs">{t.phone}</p>}
                      </div>
                    )},
                    { key: 'source', header: 'Source', render: (t: any) => {
                      if (t.pdk_holder_id || t.acs_provider === 'pdk') {
                        return (
                          <div className="flex items-center gap-1.5">
                            <Badge color="blue">PDK</Badge>
                          </div>
                        );
                      }
                      const tBrand = LOCK_BRANDS.find(b => b.key === t.acs_provider);
                      if (tBrand) return <Badge color="purple">{tBrand.name}</Badge>;
                      if (t.acs_provider === 'seam') return <Badge color="purple">Smart Lock</Badge>;
                      return <Badge color="gray">Manual</Badge>;
                    }},
                    { key: 'status', header: 'Status', render: (t: any) => (
                      <Badge status={t.status || 'active'}>{t.status || 'active'}</Badge>
                    )},
                  ]}
                  data={tenants}
                />
              )}
            </div>
          </div>
        )}

        {tab === 'audit' && (
          <div>
            {auditLoading ? (
              <div className="text-center py-8 text-gray-400">Loading audit log...</div>
            ) : auditEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No audit events yet.</div>
            ) : (
              <div className="space-y-2">
                {auditEvents.map((evt: any) => (
                  <div key={evt.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                    <div className="flex-shrink-0 mt-0.5">
                      <Badge color={EVENT_TYPE_COLORS[evt.event_type] || 'gray'}>
                        {(evt.event_type || '').replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{evt.description}</p>
                      {evt.event_source && <p className="text-xs text-gray-500">Source: {evt.event_source}</p>}
                    </div>
                    <div className="flex-shrink-0 text-xs text-gray-400">
                      {evt.created_at ? new Date(evt.created_at).toLocaleString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
